import React from 'react';
import { Lock, PauseCircle } from 'lucide-react';
import { Button } from './primitives';

/*
 * Shown whenever an order is on hold.
 *
 * Hold does two things at once — it stops the order advancing, and for anyone
 * who isn't a super admin it freezes the entire record, including edits that
 * have nothing to do with status (an address, a phone number, an internal
 * note). Without saying so, those controls would simply stop working and every
 * attempt would come back as a 422 the person can't act on.
 *
 * So the banner leads with the consequence rather than the state: what you
 * can't do, and who can undo it.
 */
const HoldBanner = ({ statusLabel, canManageHold, onResume, isPending }) => (
  <div
    role="status"
    className="flex flex-wrap items-center gap-3 mb-4 px-4 py-3 rounded-xl border border-[#CFC7E4] bg-[#F6F4FB]"
  >
    <span
      className="flex items-center justify-center w-8 h-8 rounded-lg bg-white border border-[#CFC7E4] text-[#5B4B8A] shrink-0"
      aria-hidden="true"
    >
      <PauseCircle className="w-4 h-4" />
    </span>

    <div className="min-w-0 flex-1">
      <p className="m-0 text-[13px] font-bold text-[#4A3C74]">
        This order is {statusLabel || 'on hold'}
      </p>
      <p className="m-0 text-[12px] font-medium text-[#6B5F92] mt-0.5">
        {canManageHold
          ? 'It can’t move forward until you resume it. Resuming returns it to Confirmed (or New Order / Under processing from the status menu).'
          : 'While it’s on hold, nothing on this order can be changed — including the address, contact details and notes. Only a super admin can resume it.'}
      </p>
    </div>

    {canManageHold ? (
      <Button variant="primary" onClick={onResume} disabled={isPending}>
        {isPending ? 'Working…' : 'Resume order'}
      </Button>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-[#6B5F92] shrink-0">
        <Lock className="w-3.5 h-3.5" aria-hidden="true" />
        Read-only
      </span>
    )}
  </div>
);

export default HoldBanner;
